<script lang="ts">
  import Typography from "$lib/components/Typography/Typography.svelte";
  import { onMount } from "svelte";
  import { goto } from "$app/navigation";
  import { _ } from "svelte-i18n";
  import type { SizeGuide, SizeGuideSize } from "$lib/data/sizeGuide.data";
  import Icon from "$lib/components/Icon/Icon.svelte";

  export let unit = "cm";
  export let sizeGuide: SizeGuide;
  export let fullWidth = false;
  export let modal = false;

  export let bigAndTall = false;

  let expanded = false;

  let filteredSizeGuide: SizeGuide;

  $: if (expanded || bigAndTall) {
    filteredSizeGuide = sizeGuide;
  } else {
    filteredSizeGuide = {
      headers: sizeGuide.headers,
      rows: sizeGuide.rows.filter(row => !row.bigAndTall),
    };
  }

  export let selectedSize: string;
  const renderSizeAsArray = (size: SizeGuideSize) => Array.isArray(size);

  const isBrowser = typeof window !== "undefined";
  $: if (isBrowser) {
    let url = new URL(window.location.href);
    url.searchParams.set("unit", unit);
    goto(url.href, { replaceState: true, noScroll: true });
  }

  let rowHovered: Record<string, string | string[]> = {};

  let columnHovered = "";

  let cellReferences: Record<string, HTMLElement> = {};

  let sizeRegion: string;

  if (selectedSize?.includes(" ")) {
    sizeRegion = selectedSize?.split(" ")?.[0] || sizeRegion;
    selectedSize = selectedSize?.split(" ")?.[1] || selectedSize;
  }

  onMount(() => {
    expanded = bigAndTall;
    if (!selectedSize) return;
    rowHovered = filteredSizeGuide?.rows?.find(row => {
      if (sizeRegion) {
        return row[
          Object.keys(row)?.filter(key => key.includes(sizeRegion))?.[0]
        ]?.includes(selectedSize);
      } else {
        if (selectedSize.includes("-")) {
          return Object.values(row)?.some(r => r.includes(selectedSize));
        } else {
          return Object.values(row)?.some(r => r === selectedSize);
        }
      }
    });

    setTimeout(function () {
      Object.values(cellReferences)[
        Object.keys(cellReferences)?.indexOf(
          Object.keys(cellReferences)?.find(cell => {
            if (bigAndTall) {
              return (
                cell?.includes(`-${selectedSize}`) &&
                cell?.includes("bigAndTall")
              );
            } else return cell?.includes(`-${selectedSize}`);
          }),
        )
      ]?.scrollIntoView({
        behavior: "smooth",
        block: "center",
        inline: "center",
      });
    }, 500);
  });

  let isMobile = false;
  let mdWidth = 768;
  let viewportWidth: number;

  $: isMobile = viewportWidth < mdWidth;

  function onClickExpand() {
    expanded = !expanded;
  }
</script>

<style lang="scss">
  .size-table {
    position: relative;
    width: 100%;

    border-right: 1px solid $on-surface;

    &--filler-row > td {
      border-bottom: none !important;
      height: 0px !important;
    }

    @include breakpoint(md) {
      &--size-guide {
        width: 50%;
        text-align: start !important;
        border-left: 1px solid $on-surface;
        padding: 1rem 2rem !important;

        &__content {
          border-left: 1px solid $on-surface;
          border-bottom: none !important;
          vertical-align: top;
          text-align: start !important;

          background-color: $background !important;
        }
      }

      &--fullWidth {
        width: 100%;
      }
    }

    &--table {
      margin: 0 auto;

      &--fullWidth {
        width: auto !important;
      }

      &--fullWidth-extra-row {
        width: 100%;
      }

      &--fullWidth-size-row {
        @include breakpoint(md) {
          padding: 0 3.5rem !important;
        }
        @include breakpoint(lg) {
          text-align: start;
          padding: 0 3.25rem !important;
        }

        @include breakpoint(xl) {
          text-align: start;
          padding: 0 4rem !important;
        }

        @include breakpoint(xxl) {
          text-align: start;
          padding: 0 6rem !important;
        }
        height: 120px !important;
      }
      // white-space: nowrap;
      &::-webkit-scrollbar {
        display: none;
      }
      scrollbar-width: none;
      -ms-overflow-style: none;
      table {
        border-spacing: 0;
        width: 100%;

        &::-webkit-scrollbar {
          display: none;
        }
        scrollbar-width: none;
        -ms-overflow-style: none;

        th,
        td {
          &::-webkit-scrollbar {
            display: none;
          }
          border-bottom: 1px solid $on-surface;

          &:not(:first-child) {
            min-width: 70px;
            @include breakpoint(lg) {
              min-width: 70px;
            }
          }

          &:first-child {
            @include breakpoint(lg) {
              text-align: start;
              padding: 0 3.25rem;
            }

            @include breakpoint(xl) {
              text-align: start;
              padding: 0 4rem;
            }

            @include breakpoint(xxl) {
              text-align: start;
              padding: 0 6rem;
            }
          }
          height: 48px;
          text-align: center;
          padding: 0.5rem;
          @include breakpoint(lg) {
            padding: 1rem 0.5rem;
          }
        }

        tr {
          border: 1px solid $on-surface;
          position: relative;
          &:first-child {
            border-top: 1px solid $on-surface;
          }
          border-bottom: 1px solid $on-surface;
          td {
            padding: 1rem 0.5rem;
          }
        }
      }

      &--rounded {
        padding: 2px 8px 2px 8px;
        border: 1px solid #d9d9d9bf;
        text-align: center;
        border-radius: 42px;
        min-width: 42px;
        display: inline-block;
        background-color: $background;
        white-space: nowrap;
      }

      &--margin-bottom {
        &:not(:last-child) {
          margin-bottom: 10px;
        }
      }
      &--measurements {
        text-align: start !important;
        width: calc(27% / 2) !important;
        scrollbar-width: none;
        -ms-overflow-style: none;
      }

      &--colRow {
        background-color: $background;
        transition: background-color 100ms linear;
      }
      &--hovered {
        background-color: #d9d9d9bf;
        transition: background-color 100ms linear;
      }
    }
  }

  @include breakpoint(md) {
    .size-table {
      &--table {
        table {
          table-layout: fixed;
        }
      }
    }
  }

  .modal-view {
    &:first-child {
      text-align: center !important;
      padding: 0 !important;
    }
  }

  .divider {
    background-color: $on-surface;
    text-align: start !important;
    @include breakpoint(lg) {
      padding: 0.5rem 3.25rem !important;
    }

    @include breakpoint(xl) {
      padding: 0.5rem 4rem !important;
    }

    @include breakpoint(xxl) {
      padding: 0.5rem 6rem !important;
    }
    padding: 0.5rem 2rem !important;
  }

  .expand-button {
    cursor: pointer;
  }
</style>

<svelte:window bind:innerWidth={viewportWidth} />

<div class={`size-table ${fullWidth ? "size-table--fullWidth" : ""}`}>
  <div class="size-table--table">
    <table
      class={`${fullWidth && !isMobile ? "size-table--table--fullWidth" : ""}`}
    >
      <tr>
        {#each filteredSizeGuide.headers as header}
          <th
            class={`${
              header.title.includes("Measurements")
                ? "size-table--table--measurements"
                : header
                  ? ""
                  : "thin"
            } ${
              columnHovered === header.title ? "size-table--table--hovered" : ""
            }
            ${modal ? "modal-view" : ""}
            `}
          >
            <Typography component="span" variant="overline"
              >{header.title}</Typography
            >
          </th>
        {/each}
        {#if fullWidth && !isMobile}
          <th></th>
        {/if}
        {#if !isMobile && !fullWidth}
          <th
            class={`
            size-table--size-guide
        ${modal ? "modal-view" : ""}
        `}
          >
            <Typography component="span" variant="overline"
              >{$_("Size measuring guide")}</Typography
            >
          </th>
        {/if}
      </tr>

      {#each filteredSizeGuide.rows as row, rowIndex}
        <tr
          class={`${
            row &&
            rowHovered &&
            Object.values(row)[0] === Object.values(rowHovered)[0] &&
            row.bigAndTall === rowHovered?.bigAndTall
              ? "size-table--table--hovered"
              : "size-table--table--colRow"
          }
          
          `}
          bind:this={cellReferences[
            `size-${filteredSizeGuide.headers
              ?.filter(header =>
                sizeRegion ? header?.title?.includes(sizeRegion) : true,
              )
              ?.map(header => `${row[header?.title]}`)
              ?.join("-")}${row.bigAndTall ? "-bigAndTall" : ""}`
          ]}
        >
          {#each filteredSizeGuide.headers as header}
            {#if row[header.title]}
              <td
                on:pointerenter={() => {
                  columnHovered = header.title;
                  rowHovered = row;
                }}
                on:pointerleave={() => {
                  columnHovered = "";
                  rowHovered = {};
                }}
                class={`${
                  header.title.includes("Measurements")
                    ? "size-table--table--measurements"
                    : row.rowLabel
                      ? ""
                      : "thin"
                } ${
                  columnHovered === header.title
                    ? "size-table--table--hovered"
                    : ""
                }
              ${fullWidth ? "size-table--table--fullWidth-size-row" : ""}

          ${modal ? "modal-view" : ""}
              `}
              >
                {#if renderSizeAsArray(row[header.title])}
                  {#each row[header.title] as size, index}
                    <span
                      class={`${
                        header.rounded && index < row[header.title].length - 1
                          ? "size-table--table--rounded size-table--table--margin-bottom"
                          : header.rounded
                            ? "size-table--table--rounded"
                            : ""
                      }`}
                    >
                      <Typography component="span" variant="body1">
                        {@html size}
                      </Typography></span
                    >
                    <br />
                  {/each}
                {:else}
                  <span
                    class={header.rounded ? "size-table--table--rounded" : ""}
                  >
                    <Typography component="span" variant="body2">
                      {@html row[header.title]}
                    </Typography>
                  </span>
                {/if}
              </td>
            {/if}
          {/each}
          {#if row.divider}
            <td colspan="4" class="divider">
              <span
                role="button"
                tabindex="0"
                on:click={() => onClickExpand()}
                on:keydown={() => onClickExpand()}
                class="expand-button"
              >
                <Typography
                  component="span"
                  variant="button1"
                  theme="background"
                >
                  {@html row.divider}
                </Typography>
                {#if expanded}
                  <Icon icon="expand_less" color="var(--background)" />
                {:else}
                  <Icon icon="expand_more" color="var(--background)" />
                {/if}
              </span>
            </td>
          {/if}
          {#if fullWidth && !isMobile}
            <td class="size-table--table--fullWidth-extra-row"></td>
          {/if}

          {#if !isMobile && rowIndex === 0 && !fullWidth}
            <td
              rowspan={filteredSizeGuide.rows.length * 2}
              class="size-table--size-guide__content"
            >
              <slot />
            </td>
          {/if}
        </tr>
      {/each}
      <tr class="size-table--filler-row"><td></td></tr>
    </table>
  </div>
</div>
